import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import useServiceWorker from './hooks/useServiceWorker';
import LoginForm from './components/Auth/LoginForm';
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';
import Dashboard from './components/Dashboard/Dashboard';
import AddBetForm from './components/Bets/AddBetForm';
import PendingBets from './components/Bets/PendingBets';
import BetHistory from './components/Bets/BetHistory';
import GroupsManagement from './components/Groups/GroupsManagement';
import BankrollManagement from './components/Bankroll/BankrollManagement';
import SubscriptionInfo from './components/Subscription/SubscriptionInfo';
import Reports from './components/Reports/Reports';
import UserReports from './components/Reports/UserReports';
import AdminReports from './components/Admin/AdminReports';
import SimpleReports from './components/Admin/SimpleReports';
import ExpensesManagement from './components/Expenses/ExpensesManagement';
import Goals from './components/Goals/Goals';
import AlertsSettings from './components/Alerts/AlertsSettings';
import AlertToastContainer from './components/Alerts/AlertToastContainer';
import AdminDashboard from './components/Admin/AdminDashboard';
import AccessKeyManager from './components/Admin/AccessKeyManager';
import UserManagement from './components/Admin/UserManagement';
import SalesManagement from './components/Admin/SalesManagement';
import WebhookConfig from './components/Admin/WebhookConfig';
import ExpiryWarning from './components/Layout/ExpiryWarning';
import UpdateNotification from './components/Layout/UpdateNotification';
import LandingPage from './components/Landing/LandingPage';
import CheckoutPage from './components/Checkout/CheckoutPage';
import CheckoutSuccess from './components/Checkout/CheckoutSuccess';
import CheckoutPending from './components/Checkout/CheckoutPending';
import { MessageCircle, Shield } from 'lucide-react';

// Admin App Component
const AdminApp = () => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('admin-dashboard');

  // Mostrar loading enquanto verifica autenticação (DEPOIS dos hooks!)
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Login específico para admin - RECONSTRUÍDO
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="bg-slate-800/80 backdrop-blur-md border border-slate-700 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          {/* Header do Admin */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-8 text-center">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-black text-white mb-1">
              Área Administrativa
            </h1>
            <p className="text-red-100 text-sm">
              Acesso restrito para administradores
            </p>
          </div>
          
          {/* Formulário de Login */}
          <div className="p-6">
            <LoginForm isAdminLogin={true} />
          </div>
          
          {/* Footer */}
          <div className="px-6 pb-6 text-center">
            <a
              href="/"
              className="inline-flex items-center text-cyan-400 hover:text-cyan-300 text-sm font-medium transition-colors"
            >
              ← Voltar para área de usuários
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 dark:from-gray-900 dark:to-red-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl max-w-lg w-full">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            🚫 Acesso Negado
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Você não tem permissão para acessar o painel administrativo.
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-cyan-500 text-white font-bold rounded-xl hover:bg-cyan-600 transition-colors"
          >
            Voltar para o Dashboard
          </a>
        </div>
      </div>
    );
  }

  const renderAdminContent = () => {
    switch (activeTab) {
      case 'manage-users':
        return <UserManagement setActiveTab={setActiveTab} />;
      case 'sales':
        return <SalesManagement />;
      case 'webhook':
        return <WebhookConfig />;
      case 'admin-dashboard':
        return <AdminDashboard setActiveTab={setActiveTab} />;
      case 'reports':
        return <AdminReports />;
      case 'settings':
        return (
          <div className="p-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              ⚙️ Configurações
            </h1>
            <AccessKeyManager />
          </div>
        );
      default:
        return <AdminDashboard setActiveTab={setActiveTab} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="flex pt-16">
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
        <main className="flex-1 lg:ml-64 p-2">
          {renderAdminContent()}
        </main>
      </div>
      
      {/* Alert Toast Notifications */}
      <AlertToastContainer />
      
      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/5511916560595"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-50 group"
        aria-label="Fale conosco no WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
        <span className="absolute right-full mr-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Fale Conosco
        </span>
      </a>
    </div>
  );
};

const AppContent = () => {
  const { isAuthenticated, isKeyExpired, isAdmin, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const navigate = useNavigate();

  // Se for admin, redirecionar para /adm usando React Router (sem page reload)
  React.useEffect(() => {
    if (isAuthenticated && isAdmin && window.location.pathname === '/') {
      navigate('/adm', { replace: true });
    }
  }, [isAuthenticated, isAdmin, navigate]);

  // Mostrar loading enquanto verifica autenticação (DEPOIS dos hooks!)
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm isAdminLogin={false} />; // Força modo usuário, sem tab switcher
  }

  if (isKeyExpired && !isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 dark:from-gray-900 dark:to-red-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl max-w-lg w-full mx-4 border-t-4 border-red-500">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              🚨 Acesso Bloqueado
            </h1>
            <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
              Licença Expirada
            </h2>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                <strong>Para continuar usando o Stake Mind:</strong>
              </p>
              <ul className="text-left text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>• Entre em contato pelo WhatsApp</li>
                <li>• Solicite a renovação da sua chave</li>
                <li>• Mantenha todos os seus dados salvos</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <a
                href="https://wa.me/5511916560595?text=Ol%C3%A1%21%20Minha%20assinatura%20do%20Stake%20Mind%20expirou.%20Gostaria%20de%20renovar%20minha%20chave%20de%20acesso%20URGENTE."
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 inline-flex items-center justify-center space-x-2 animate-bounce"
              >
                <MessageCircle className="w-5 h-5" />
                <span>RENOVAR AGORA VIA WHATSAPP</span>
              </a>
              
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gray-600 text-white py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors text-sm"
              >
                🔄 Recarregar após renovação
              </button>
            </div>
            
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-6">
              Seus dados estão seguros e serão mantidos após a renovação
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    console.log('🔍 renderContent - activeTab:', activeTab);
    
    switch (activeTab) {
      case 'user-reports':
        return <UserReports />;
      case 'add-bet':
        return <AddBetForm />;
      case 'pending-bets':
        return <PendingBets />;
      case 'bet-history':
        return <BetHistory />;
      case 'groups':
        return <GroupsManagement />;
      case 'bankroll':
        return <BankrollManagement />;
      case 'expenses':
        return <ExpensesManagement />;
      case 'goals':
        return <Goals />;
      case 'alerts-settings':
        return <AlertsSettings />;
      case 'subscription':
        return <SubscriptionInfo />;
      case 'dashboard':
        return <Dashboard setActiveTab={setActiveTab} />;
      default:
        console.log('🔍 Default case - isAdmin:', isAdmin);
        return isAdmin ? <AdminDashboard setActiveTab={setActiveTab} /> : <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <ExpiryWarning />
      <UpdateNotification />
      <div className="flex">
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
        <main className="flex-1 p-6">
          {renderContent()}
        </main>
      </div>
      
      {/* Alert Toast Notifications */}
      <AlertToastContainer />
    </div>
  );
};

function App() {
  // Registra e gerencia Service Worker
  const { updateAvailable, isOnline } = useServiceWorker();

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Rota pública para landing page */}
            <Route path="/assinar" element={<LandingPage />} />
            
            {/* Rotas públicas para checkout */}
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/pending" element={<CheckoutPending />} />
            
            {/* Rotas de administração */}
            <Route path="/adm/*" element={<AdminApp />} />
            
            {/* Rotas da aplicação de usuário */}
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;